import parse from 'autosuggest-highlight/parse';
import debounce from 'lodash.debounce';
import React, { useCallback, useRef, useState } from 'react';
import { MapPin } from 'react-feather';
import AsyncSelect from 'react-select/async';

import useScript from '../../hooks/useScript';
import ClearIndicator from './selects/ClearIndicator';
import styles, { theme } from './selects/defaultSelectStyles';
import DropdownIndicator from './selects/DropdownIndicator';
import NoOptionsMessage from './selects/NoOptionsMessage';

const customComponents = {
    DropdownIndicator,
    NoOptionsMessage,
    ClearIndicator
};

export default function GoogleAutocomplete({ onPlaceSelected }) {
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const autocompleteService = useRef();
    useScript('https://maps.googleapis.com/maps/api/js?key=AIzaSyCjKWG4SK7rrbPSqO-ZDx51Ob9VPyi32pM&libraries=places');

    const handleInputChange = (newValue) => {
        setInputValue(newValue);
    };

    const fetchPlaces = async (query, callback) => {
        if (!autocompleteService.current && window.google) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return callback([]);
        }
        const requestOptions = {
            input: query,
            location: new window.google.maps.LatLng(46.0770413127077, 14.749145507812498),
            radius: 150000,
            language: 'sl'
        };

        return autocompleteService.current.getPlacePredictions(requestOptions, callback);
    };

    const debouncedFetch = useCallback(
        debounce((query, callback) => fetchPlaces(query, callback), 350),
        []
    );

    const loadOptions = (query) =>
        new Promise((resolve) => {
            if (!query) return resolve([]);
            setIsLoading(true);
            return debouncedFetch(query, (results) => {
                setIsLoading(false);
                return resolve(results);
            });
        });

    const handleOptionChange = (option) => {
        onPlaceSelected(option);
    };

    const getOptionLabel = useCallback((option) => (typeof option === 'string' ? option : option.description));

    const renderOption = useCallback((option, { context }) => {
        if (context === 'value') return option?.description ?? null;

        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
            <div className="flex gap-1 align-center">
                <div className="flex-shrink-0">
                    <MapPin width={25} height={35} />
                </div>
                <div className="leading-tight">
                    {parts.map((part, index) => (
                        <span key={index} className={`${part.highlight ? 'font-semibold' : 'font-normal'}`}>
                            {part.text}
                        </span>
                    ))}

                    <div className="text-secondary text-sm leading-tight">
                        {option.structured_formatting?.secondary_text}
                    </div>
                </div>
            </div>
        );
    }, []);

    return (
        <AsyncSelect
            id="google-autocomplete-search"
            inputId="google-autocomplete-input"
            placeholder="Poišči lokacijo ..."
            components={customComponents}
            styles={styles}
            theme={theme}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            blurInputOnSelect
            isClearable
            cacheOptions
            loadOptions={loadOptions}
            getOptionLabel={getOptionLabel}
            onChange={handleOptionChange}
            isLoading={isLoading}
            formatOptionLabel={renderOption}
        />
    );
}
